import confetti from 'canvas-confetti';

interface MeetingMeta {
    season: number;
    number: number;
    code: string;
    weekLabel: string;
    time: string;
    date: string;
    label: string;
    missedMessage?: string;
}

interface GoalsCheckInConfig {
    ajaxUrl: string;
    nonce: string;
    state: string;
    meetingDate: string | null;
    reportMeetingDate?: string | null;
    canReportMissed?: boolean;
    openAt: string;
    serverNow?: string;
    checkinOpenAt?: string;
    closeAt: string;
    meetingStartAt: string;
    nextMeetingDate: string;
    existing: {
        status: string;
        pushups: number;
        minute?: number;
        minutes_early?: number;
        in_grace?: boolean;
        just_made_it?: boolean;
        time: string;
        timestamp: string;
        message: string;
        note?: string;
        checkin_sent?: boolean;
        checkin_sent_at?: string;
        checkInAt?: string;
        sentConfirmAvailableAt?: string;
        meeting_date?: string;
    } | null;
    bypassWindow?: boolean;
    previewMode?: boolean;
    meetingMeta?: MeetingMeta | null;
    missedSeasonCount?: number;
    userName?: string;
    sentConfirmDelayMinutes?: number;
    liveBoardEnabled?: boolean;
    liveBoardMeetingDate?: string | null;
    liveBoardPhase?: string;
    liveBoardPoll?: boolean;
    liveBoardPreviewWaiting?: boolean;
    liveBoardPreviewSamples?: boolean;
    livePollMs?: number;
    basePushups?: number;
    flatWindowMin?: number;
    gracePeriodMs?: number;
    meetingUrl?: string;
    followsCurrent?: boolean;
}

interface CheckInAjaxResponse {
    success: boolean;
    data: {
        status?: string;
        pushups?: number;
        minute?: number;
        minutes_early?: number;
        in_grace?: boolean;
        just_made_it?: boolean;
        time?: string;
        timestamp?: string;
        message?: string;
        note?: string;
        checkedInAt?: string;
        checkin_sent?: boolean;
        checkin_sent_at?: string;
        checkInAt?: string;
        sentConfirmAvailableAt?: string;
        meeting_date?: string;
        meeting_url?: string;
        missedSeasonCount?: number;
    };
}

interface WindowStatePollResponse {
    state: string;
    serverNow: string;
    meetingDate: string | null;
    reportMeetingDate?: string | null;
    canReportMissed?: boolean;
    openAt: string;
    checkinOpenAt?: string;
    closeAt: string;
    meetingStartAt: string;
    nextMeetingDate?: string | null;
    existing: GoalsCheckInConfig['existing'];
    bypassWindow?: boolean;
    liveBoardPhase?: string;
}

type PreviewMode = 'open_on_time' | 'open_late' | 'missed_report' | 'success_on_time' | 'success_late' | 'success_missed';

const PREVIEW_MODES: PreviewMode[] = ['open_on_time', 'open_late', 'missed_report', 'success_on_time', 'success_late', 'success_missed'];

const CALENDAR_CHECK_PATHS =
    '<path d="M8 2v4"/><path d="M16 2v4"/><rect x="3" y="4" width="18" height="18"/><path d="M3 10h18"/><path d="m8 16 2.5 2.5 5.5-5.5"/>';

function pendingIconForStatus(status: 'on_time' | 'late' | 'missed'): string {
    if (status === 'late') {
        return WARNING_ICON;
    }
    const colorClass = status === 'missed' ? 'check-in-hero__icon--missed' : 'check-in-hero__icon--pending';
    return `<svg class="check-in-hero__icon ${colorClass}" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${CALENDAR_CHECK_PATHS}</svg>`;
}

const CHECKMARK_ICON = `<svg class="check-in-hero__icon check-in-hero__icon--on-time" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>`;

const WARNING_ICON = `<svg class="check-in-hero__icon check-in-hero__icon--late" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>`;

const MISSED_ICON = `<svg class="check-in-hero__icon check-in-hero__icon--missed" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>`;

const MISSED_SENT_ICON = `<svg class="check-in-hero__icon check-in-hero__icon--missed-sent" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>`;

const LATE_NOTE_INPUT_ATTRS = 'required data-parsley-required="true" data-parsley-required-message="Please enter a reason for being late."';

const MISSED_NOTE_INPUT_ATTRS = 'required data-parsley-required="true" data-parsley-required-message="Please enter a reason for missing."';

type ParsleyLike = { validate: () => boolean; reset: () => void; destroy?: () => void };
type JQueryParsley = (target: string | Element) => { parsley: () => ParsleyLike };

let clockInterval: ReturnType<typeof setInterval> | null = null;
let syncInterval: ReturnType<typeof setInterval> | null = null;
let visibilityTick: (() => void) | null = null;
let noteForce: 'show' | 'hide' | null = null;
let checkInStep: 'idle' | 'late_note' = 'idle';
let currentPreview: PreviewMode | null = null;
let cardLoadRevealDone = false;
let includeCardLoadOverlay = true;
let sentCheckinInterval: ReturnType<typeof setInterval> | null = null;
let serverOffsetMs = 0;
let windowStatePollInterval: ReturnType<typeof setInterval> | null = null;
let windowStatePollConfig: GoalsCheckInConfig | null = null;

const CARD_LOAD_SPINNER_MS = 300;
const CARD_LOAD_BORDER_MS = 750;
const CARD_LOAD_SPINNER_FADE_MS = 150;
const CARD_LOAD_HOLD_MS = CARD_LOAD_SPINNER_MS + CARD_LOAD_BORDER_MS - CARD_LOAD_SPINNER_FADE_MS;
const CARD_LOAD_OVERLAY_FADE_MS = 500;
const CARD_LOAD_FADE_MS = CARD_LOAD_OVERLAY_FADE_MS;

const CARD_LOAD_SPINNER_HTML =
    '<span class="check-in-hero__spinner"><svg class="circular" viewBox="25 25 50 50"><circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miterlimit="10"></circle></svg></span>';

function cardLoadOverlayHtml(): string {
    if (!includeCardLoadOverlay) {
        return '';
    }
    return `<div class="check-in-hero__load-overlay" aria-hidden="true">${CARD_LOAD_SPINNER_HTML}</div>`;
}

function markCheckInCardsReady(root: ParentNode = document): void {
    const stage =
        root instanceof Document ? root.getElementById('check-in-preview-stage') : (root as ParentNode).querySelector('#check-in-preview-stage') || root;
    if (!stage) return;
    stage.querySelectorAll('.check-in-hero__card').forEach(card => {
        card.classList.add('check-in-hero__card--ready');
    });
}

function sentConfirmDelayMs(config: GoalsCheckInConfig): number {
    return (config.sentConfirmDelayMinutes ?? 5) * 60 * 1000;
}

function sentConfirmAvailableAt(data: CheckInAjaxResponse['data'], config: GoalsCheckInConfig): number {
    if (data.sentConfirmAvailableAt) {
        return new Date(data.sentConfirmAvailableAt).getTime();
    }
    if (data.checkInAt) {
        return new Date(data.checkInAt).getTime() + sentConfirmDelayMs(config);
    }
    return Date.now() + sentConfirmDelayMs(config);
}

function sentConfirmHintText(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    if (totalSeconds >= 3600) {
        const hours = Math.ceil(totalSeconds / 3600);
        return hours === 1 ? 'Available in 1 hour' : `Available in ${hours} hours`;
    }
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `Available in ${mins}:${String(secs).padStart(2, '0')}`;
}

function clearSentCheckinInterval(): void {
    if (sentCheckinInterval) {
        clearInterval(sentCheckinInterval);
        sentCheckinInterval = null;
    }
}

function sentCheckinButtonHtml(config: GoalsCheckInConfig): string {
    const delayMs = sentConfirmDelayMs(config);
    const disabled = delayMs > 0 ? ' disabled' : '';
    const hint = delayMs > 0 ? '<p class="check-in-sent-hint" id="check-in-sent-hint" aria-live="polite"></p>' : '';
    return `<button type="button" id="check-in-sent-btn" class="btn btn-primary btn-block btn-lg check-in-hero__btn"${disabled}>Sent check-in</button>${hint}`;
}

function initSentCheckinCooldown(config: GoalsCheckInConfig, data: CheckInAjaxResponse['data']): void {
    clearSentCheckinInterval();
    const btn = document.getElementById('check-in-sent-btn') as HTMLButtonElement | null;
    const hint = document.getElementById('check-in-sent-hint');
    if (!btn || data.checkin_sent) return;
    if (sentConfirmDelayMs(config) <= 0) {
        btn.disabled = false;
        if (hint) {
            hint.textContent = '';
            hint.hidden = true;
        }
        return;
    }
    const unlockAt = sentConfirmAvailableAt(data, config);
    const tick = () => {
        const remaining = unlockAt - Date.now();
        if (remaining <= 0) {
            btn.disabled = false;
            if (hint) {
                hint.textContent = '';
                hint.hidden = true;
            }
            clearSentCheckinInterval();
            return;
        }
        btn.disabled = true;
        if (hint) {
            hint.hidden = false;
            hint.textContent = sentConfirmHintText(remaining);
        }
    };
    tick();
    sentCheckinInterval = setInterval(tick, 1000);
}

function initCardLoadReveal(): Promise<void> {
    if (cardLoadRevealDone) {
        return Promise.resolve();
    }
    const stage = document.getElementById('check-in-preview-stage');
    if (!stage) {
        cardLoadRevealDone = true;
        includeCardLoadOverlay = false;
        return Promise.resolve();
    }
    const cards = Array.from(stage.querySelectorAll('.check-in-hero__card:not(.check-in-hero__card--ready)')).filter(card =>
        card.querySelector('.check-in-hero__load-overlay'),
    );
    if (!cards.length) {
        stage.querySelectorAll('.check-in-hero__card').forEach(card => {
            if (card.querySelector('.check-in-hero__load-overlay')) {
                card.classList.add('check-in-hero__card--ready');
            }
        });
        cardLoadRevealDone = true;
        includeCardLoadOverlay = false;
        return Promise.resolve();
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        cards.forEach(card => {
            card.classList.add('check-in-hero__card--ready');
        });
        cardLoadRevealDone = true;
        includeCardLoadOverlay = false;
        return Promise.resolve();
    }
    return new Promise(resolve => {
        window.setTimeout(() => {
            let pending = cards.length;
            const finishOne = () => {
                pending -= 1;
                if (pending <= 0) {
                    cardLoadRevealDone = true;
                    includeCardLoadOverlay = false;
                    resolve();
                }
            };
            cards.forEach(card => {
                const overlay = card.querySelector('.check-in-hero__load-overlay') as HTMLElement | null;
                card.classList.add('check-in-hero__card--ready');
                if (!overlay) {
                    finishOne();
                    return;
                }
                let finished = false;
                const complete = () => {
                    if (finished) return;
                    finished = true;
                    finishOne();
                };
                overlay.addEventListener(
                    'transitionend',
                    e => {
                        if (e.propertyName === 'opacity') {
                            complete();
                        }
                    },
                    { once: true },
                );
                window.setTimeout(complete, CARD_LOAD_FADE_MS + 100);
            });
        }, CARD_LOAD_HOLD_MS);
    });
}

function formatEasternClock(d: Date): string {
    return (
        d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'America/New_York',
        }) + ' ET'
    );
}

function syncServerClock(serverNow?: string): void {
    if (!serverNow) return;
    const parsed = Date.parse(serverNow);
    if (Number.isNaN(parsed)) return;
    serverOffsetMs = parsed - Date.now();
}

function nowMs(): number {
    return Date.now() + serverOffsetMs;
}

function formatMockTimestamp(): string {
    return (
        new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'America/New_York',
        }) + ' ET'
    );
}

function formatMockFullTimestamp(): string {
    return (
        new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'America/New_York',
        }) + ' ET'
    );
}

function formatMockReportedTimestamp(): string {
    return (
        new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/New_York',
        }) + ' ET'
    );
}

function pushupsDisplayText(count: number): string {
    const label = count === 1 ? 'Push-Up!' : 'Push-Ups!';
    return `${count} ${label}`;
}

function normalizeStatus(status: string): string {
    if (status === 'extra_late' || status === 'extra-late' || status === 'extra late') {
        return 'late';
    }
    return status;
}

function gracePeriodMs(config: GoalsCheckInConfig): number {
    return config.gracePeriodMs ?? 60000;
}

function computePushupsForConfig(config: GoalsCheckInConfig, now = new Date()): number {
    const startAt = meetingStartMs(config);
    if (startAt === null) return 0;
    const base = config.basePushups ?? 20;
    const flat = config.flatWindowMin ?? 5;
    const currentMs = now.getTime();
    const graceEnd = startAt + gracePeriodMs(config);
    if (currentMs < graceEnd) return 0;
    const secondsLate = Math.max(0, (currentMs - startAt) / 1000);
    const minute = Math.max(1, Math.ceil(secondsLate / 60));
    if (minute <= flat) return base;
    return base + (minute - flat);
}

function escHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function isValidPreview(value: string | null): value is PreviewMode {
    return value !== null && PREVIEW_MODES.includes(value as PreviewMode);
}

function getPreviewFromUrl(): PreviewMode | null {
    const value = new URLSearchParams(window.location.search).get('preview');
    return isValidPreview(value) ? value : null;
}

function updatePreviewUrl(preview: PreviewMode): string {
    const url = new URL(window.location.href);
    url.searchParams.set('preview', preview);
    return url.pathname + url.search + url.hash;
}

function setPreviewUrl(preview: PreviewMode): void {
    history.replaceState(null, '', updatePreviewUrl(preview));
}

function statusClass(status: string): string {
    status = normalizeStatus(status);
    if (status === 'on_time') return 'on-time';
    if (status === 'missed') return 'missed';
    return 'late';
}

function heroBadgeStatusClass(status: string, _justMadeIt = false): string {
    return statusClass(status);
}

function statusLabel(status: string): string {
    status = normalizeStatus(status);
    if (status === 'on_time') return 'On-time';
    if (status === 'missed') return 'Missed';
    return 'Late';
}

function formatLateMinutes(minutes: number): string {
    return String(Math.max(1, Math.round(minutes)));
}

function formatLateSummary(minutesLate: number, pushups?: number): string {
    const parts: string[] = [];
    if (minutesLate > 0) {
        parts.push(`${formatLateMinutes(minutesLate)}m`);
    }
    if (pushups && pushups > 0) {
        parts.push(`${pushups}p`);
    }
    if (parts.length === 0) {
        return 'Late';
    }
    return `Late (${parts.join(', ')})`;
}

function onTimeDisplayLabel(_minutesEarly = 0, _justMadeIt = false): string {
    return 'On-time';
}

function openBadgeLabel(
    status: 'on_time' | 'late' | 'missed',
    minutesLate = 0,
    minutesEarly = 0,
    beforeOpen = false,
    opensInLabel = '',
    justMadeIt = false,
    pushups?: number,
): string {
    if (status === 'missed') return 'Missed';
    if (status === 'late') return formatLateSummary(minutesLate, pushups);
    if (beforeOpen && opensInLabel) return opensInLabel;
    return onTimeDisplayLabel(minutesEarly, justMadeIt);
}

function openClockText(now: Date): string {
    return formatEasternClock(now);
}

function meetingStartMs(config: GoalsCheckInConfig): number | null {
    if (!config.meetingStartAt) return null;
    const startAt = new Date(config.meetingStartAt).getTime();
    return Number.isNaN(startAt) ? null : startAt;
}

function checkinOpenMs(config: GoalsCheckInConfig): number | null {
    const raw = config.checkinOpenAt || config.openAt;
    if (!raw) return null;
    const openAt = new Date(raw).getTime();
    return Number.isNaN(openAt) ? null : openAt;
}

function opensInLabel(config: GoalsCheckInConfig): string {
    const openMs = checkinOpenMs(config);
    if (openMs === null) return '';
    const seconds = Math.max(0, Math.ceil((openMs - nowMs()) / 1000));
    if (seconds <= 0) return '';
    if (seconds >= 86400) {
        const days = Math.max(1, Math.ceil(seconds / 86400));
        return `Opens in ${days} ${days === 1 ? 'day' : 'days'}`;
    }
    if (seconds >= 3600) {
        const hours = Math.max(1, Math.ceil(seconds / 3600));
        return `Opens in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `Opens in ${mins}:${String(secs).padStart(2, '0')}`;
}

function isBeforeCheckinOpen(config: GoalsCheckInConfig): boolean {
    const openMs = checkinOpenMs(config);
    if (openMs === null) return false;
    return nowMs() < openMs;
}

function minutesEarlyForConfig(config: GoalsCheckInConfig): number {
    const startAt = meetingStartMs(config);
    const openMs = checkinOpenMs(config);
    if (startAt === null || openMs === null) return 25;
    const currentMs = nowMs();
    if (currentMs < openMs || currentMs >= startAt) return 0;
    return Math.round(((startAt - currentMs) / 60000) * 10) / 10;
}

function isJustMadeIt(config: GoalsCheckInConfig): boolean {
    const startAt = meetingStartMs(config);
    if (startAt === null) return false;
    const currentMs = nowMs();
    return currentMs > startAt && currentMs < startAt + gracePeriodMs(config);
}

function isGracePeriod(config: GoalsCheckInConfig): boolean {
    const startAt = meetingStartMs(config);
    if (startAt === null) return false;
    const currentMs = nowMs();
    return currentMs >= startAt && currentMs < startAt + gracePeriodMs(config);
}

function minutesLateForConfig(config: GoalsCheckInConfig): number {
    if (config.previewMode && currentPreview === 'open_late') return 15;
    const startAt = meetingStartMs(config);
    if (startAt === null) return 0;
    const currentMs = nowMs();
    const lateThreshold = startAt + gracePeriodMs(config);
    if (currentMs < lateThreshold) return 0;
    return Math.round(((currentMs - startAt) / 60000) * 10) / 10;
}

function heroTitleForStatus(status: string): string {
    if (normalizeStatus(status) === 'late') return 'Late Check-In';
    if (status === 'missed') return 'Missed Check-In';
    return 'Check in';
}

function heroTitleForLiveOpen(config: GoalsCheckInConfig, status: string, beforeOpen: boolean): string {
    if (beforeOpen) return 'Check-in opens soon';
    return heroTitleForStatus(status);
}

function liveOpenBadgeState(config: GoalsCheckInConfig, status: 'on_time' | 'late' | 'missed') {
    if (status === 'missed') {
        return { minutesLate: 0, minutesEarly: 0, justMadeIt: false, beforeOpen: false, opensInLabel: '' };
    }
    if (status === 'late') {
        return { minutesLate: minutesLateForConfig(config), minutesEarly: 0, justMadeIt: false, beforeOpen: false, opensInLabel: '' };
    }
    const beforeOpen = isBeforeCheckinOpen(config);
    const justMadeIt = isJustMadeIt(config);
    return {
        minutesLate: 0,
        minutesEarly: beforeOpen || justMadeIt ? 0 : minutesEarlyForConfig(config),
        justMadeIt,
        beforeOpen,
        opensInLabel: beforeOpen ? opensInLabel(config) : '',
    };
}

function welcomeHeadline(name?: string): string {
    const display = (name || '').trim() || 'there';
    return `Welcome ${display}!`;
}

function headlineForStatus(status: string, minute?: number, missedMessage?: string, userName?: string): string {
    if (status === 'on_time' || normalizeStatus(status) === 'late') {
        return welcomeHeadline(userName);
    }
    if (status === 'missed') return missedMessage || "We'll see you next week.";
    return welcomeHeadline(userName);
}

function missedSuccessHeadline(checkinSent = false): string {
    return checkinSent ? 'Check-In Sent!' : 'Reminder: Send Check-In';
}

function checkInStageRoot(root: ParentNode = document): ParentNode {
    if (root instanceof Document) {
        return root.getElementById('check-in-preview-stage') || root;
    }
    return root.querySelector('#check-in-preview-stage') || root;
}

function getCheckInForm(root: ParentNode = document): HTMLFormElement | null {
    return checkInStageRoot(root).querySelector('.check-in-form') as HTMLFormElement | null;
}

function getCheckInNoteField(form: HTMLFormElement): HTMLInputElement | null {
    return (form.querySelector('#check-in-missed-note') || form.querySelector('#check-in-note')) as HTMLInputElement | null;
}

function noteFieldIsRequired(field: HTMLInputElement | null): boolean {
    if (!field) return false;
    if (field.hidden) return false;
    const label = field.closest('.check-in-note');
    if (!label || label.classList.contains('check-in-note--hidden') || label.hasAttribute('hidden')) {
        return false;
    }
    return field.hasAttribute('required') || field.getAttribute('data-parsley-required') === 'true';
}

function setCheckInNoteError(field: HTMLInputElement | null, hasError: boolean): void {
    if (!field) return;
    field.classList.toggle('parsley-error', hasError);
}

function checkInJquery(): JQueryParsley | null {
    const win = window as Window & {
        $j?: JQueryParsley & { fn?: { parsley?: unknown } };
        jQuery?: JQueryParsley & { fn?: { parsley?: unknown } };
    };
    for (const jq of [win.$j, win.jQuery]) {
        if (jq && typeof jq.fn?.parsley === 'function') {
            return jq;
        }
    }
    return null;
}

function initCheckInParsley(root: ParentNode = document): void {
    const jq = checkInJquery();
    const form = getCheckInForm(root);
    if (!jq || !form) return;
    const existing = jq(form).parsley();
    if (existing && typeof existing.destroy === 'function') {
        existing.destroy();
    }
    jq(form).parsley();
}

function bindCheckInNoteValidation(root: ParentNode = document): void {
    const form = getCheckInForm(root);
    const noteField = form ? getCheckInNoteField(form) : null;
    if (!noteField || noteField.dataset.checkInNoteBound === '1') return;
    noteField.dataset.checkInNoteBound = '1';
    noteField.addEventListener('input', () => {
        if (noteField.value.trim()) {
            setCheckInNoteError(noteField, false);
        }
    });
}

function validateCheckInForm(root: ParentNode = document): boolean {
    const form = getCheckInForm(root);
    if (!form) return false;
    const noteField = getCheckInNoteField(form);
    const noteRequired = noteFieldIsRequired(noteField);
    const noteEmpty = !noteField?.value.trim();
    if (noteRequired && noteEmpty) {
        const jq = checkInJquery();
        if (jq) {
            initCheckInParsley(root);
            const instance = jq(form).parsley();
            if (instance && typeof instance.validate === 'function') {
                instance.validate();
            }
            if (noteField) {
                jq(noteField).parsley().validate();
            }
        } else {
            form.reportValidity();
        }
        setCheckInNoteError(noteField, true);
        noteField?.focus();
        return false;
    }
    setCheckInNoteError(noteField, false);
    if (!noteRequired) {
        return true;
    }
    const jq = checkInJquery();
    if (!jq) {
        if (form.checkValidity()) return true;
        form.reportValidity();
        return false;
    }
    initCheckInParsley(root);
    const instance = jq(form).parsley();
    if (instance && typeof instance.validate === 'function' && instance.validate()) {
        return true;
    }
    if (noteField) {
        jq(noteField).parsley().validate();
        setCheckInNoteError(noteField, !noteField.value.trim());
    }
    return false;
}

function iconForMissed(data: CheckInAjaxResponse['data']): string {
    return data.checkin_sent ? MISSED_SENT_ICON : MISSED_ICON;
}

function cardVariantForStatus(status: string): 'on_time' | 'late' | 'missed' {
    if (status === 'on_time') return 'on_time';
    if (status === 'missed') return 'missed';
    return 'late';
}

function cardClassForVariant(variant: 'on_time' | 'late' | 'missed'): string {
    if (variant === 'on_time') return 'check-in-hero__card--on-time';
    if (variant === 'missed') return 'check-in-hero__card--missed';
    return 'check-in-hero__card--late';
}

function iconForVariant(variant: 'on_time' | 'late' | 'missed'): string {
    if (variant === 'on_time') return CHECKMARK_ICON;
    if (variant === 'missed') return MISSED_ICON;
    return WARNING_ICON;
}

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isLateWindow(config: GoalsCheckInConfig): boolean {
    const startAt = new Date(config.meetingStartAt).getTime();
    return nowMs() >= startAt + gracePeriodMs(config);
}

function liveStatusForOpen(config: GoalsCheckInConfig): 'on_time' | 'late' {
    if (config.previewMode && currentPreview === 'open_late') return 'late';
    if (config.previewMode && currentPreview === 'open_on_time') return 'on_time';
    if (noteForce === 'show') return 'late';
    if (noteForce === 'hide') return 'on_time';
    return isLateWindow(config) ? 'late' : 'on_time';
}

function buildTitleHtml(status = 'on_time'): string {
    return `<h2 class="check-in-hero__title">${escHtml(heroTitleForStatus(status))}</h2>`;
}

function buildIdentityHtml(config: GoalsCheckInConfig, iconHtml: string, titleStatus = 'on_time', titleOverride?: string): string {
    const title = titleOverride ?? heroTitleForStatus(titleStatus);
    return `
        <div class="check-in-hero__identity">
            ${iconHtml}
            <h2 class="check-in-hero__title">${escHtml(title)}</h2>
            ${buildMetaHtml(config)}
        </div>`;
}

function buildMissedSeasonHtml(count: number): string {
    const label = count === 1 ? '1 missed this season' : `${count} missed this season`;
    return `<p class="check-in-hero__season-missed">${escHtml(label)}</p>`;
}

function buildLiveHtml(
    status: 'on_time' | 'late' | 'missed',
    includeClock = true,
    minutesLate = 0,
    missedSeasonCount = 0,
    minutesEarly = 0,
    beforeOpen = false,
    opensInLabelText = '',
    justMadeIt = false,
): string {
    const clock = includeClock && status !== 'missed' ? buildOpenClockHtml(status === 'late' ? 'late' : 'on_time') : '';
    const seasonMissed = status === 'missed' && missedSeasonCount > 0 ? buildMissedSeasonHtml(missedSeasonCount) : '';
    return `
        <div class="check-in-hero__live">
            ${buildStatusBadgeHtml(status, minutesLate, minutesEarly, beforeOpen, opensInLabelText, justMadeIt)}
            ${seasonMissed}
            ${clock}
        </div>`;
}

function buildOnTimeSuccessLiveHtml(minutesEarly = 0, justMadeIt = false): string {
    return `
        <div class="check-in-hero__live">
            ${buildStatusBadgeHtml('on_time', 0, minutesEarly, false, '', justMadeIt, true)}
        </div>`;
}

function buildMissedSuccessLiveHtml(note: string, missedSeasonCount = 0): string {
    const seasonMissed = missedSeasonCount > 0 ? buildMissedSeasonHtml(missedSeasonCount) : '';
    return `
        <div class="check-in-hero__live">
            ${buildStatusBadgeHtml('missed', 0, 0, false, '', false, true)}
            ${note ? `<p class="check-in-live-reason check-in-live-reason--missed">${escHtml(note)}</p>` : ''}
            ${seasonMissed}
        </div>`;
}

function buildLateSuccessLiveHtml(status: string, minute: number, note: string, pushups: number): string {
    return `
        <div class="check-in-hero__live">
            ${buildStatusBadgeHtml('late', minute, 0, false, '', false, true, pushups)}
            ${note ? `<p class="check-in-live-reason">${escHtml(note)}</p>` : ''}
        </div>`;
}

function buildStatusBadgeHtml(
    status: 'on_time' | 'late' | 'missed',
    minutesLate = 0,
    minutesEarly = 0,
    beforeOpen = false,
    opensInLabelText = '',
    justMadeIt = false,
    replayable = false,
    pushups?: number,
): string {
    const badgeClass = replayable ? 'check-in-success__badge check-in-success__replay' : 'check-in-success__badge';
    const badgeAttrs = replayable ? ' role="button" tabindex="0" aria-label="Replay celebration"' : '';
    return `<span class="check-in-hero__status check-in-hero__status--${escHtml(heroBadgeStatusClass(status, justMadeIt))}" id="check-in-live-status"><span class="${badgeClass}"${badgeAttrs}>${escHtml(openBadgeLabel(status, minutesLate, minutesEarly, beforeOpen, opensInLabelText, justMadeIt, pushups))}</span></span>`;
}

function buildOpenClockHtml(status: 'on_time' | 'late'): string {
    const lateClass = status === 'late' ? ' check-in-open-clock--late' : '';
    return `<p class="check-in-open-clock check-in-open-clock--${escHtml(statusClass(status))}${lateClass}" id="check-in-open-clock"></p>`;
}

function syncLivePushups(config: GoalsCheckInConfig, status: string): void {
    document.getElementById('check-in-live-pushups')?.remove();
    if (status !== 'late') return;
    const badge = document.querySelector('#check-in-live-status .check-in-success__badge');
    if (!badge) return;
    badge.textContent = formatLateSummary(minutesLateForConfig(config), computePushupsForConfig(config));
}

function syncLiveIcon(status: 'on_time' | 'late' | 'missed', mode: 'open' | 'missed' = 'open'): void {
    const identity = document.querySelector('.check-in-hero__identity');
    if (!identity) return;
    const existing = identity.querySelector('.check-in-hero__icon');
    if (existing) existing.remove();
    const iconHtml = mode === 'missed' ? MISSED_ICON : pendingIconForStatus(status);
    identity.insertAdjacentHTML('afterbegin', iconHtml);
}

function syncLiveStatus(config: GoalsCheckInConfig, mode: 'open' | 'missed' = 'open'): void {
    const badgeWrap = document.getElementById('check-in-live-status');
    const clockEl = document.getElementById('check-in-open-clock');
    const card = document.querySelector('.check-in-hero__card');
    if (!card) return;
    const status = mode === 'missed' ? 'missed' : liveStatusForOpen(config);
    const badgeState = liveOpenBadgeState(config, status);
    const titleEl = document.querySelector('.check-in-hero__title');
    if (titleEl) titleEl.textContent = mode === 'missed' ? heroTitleForStatus('missed') : heroTitleForLiveOpen(config, status, badgeState.beforeOpen);
    if (badgeWrap) {
        badgeWrap.className = `check-in-hero__status check-in-hero__status--${heroBadgeStatusClass(status, badgeState.justMadeIt)}`;
        const badge = badgeWrap.querySelector('.check-in-success__badge');
        if (badge) {
            const pushups = status === 'late' ? computePushupsForConfig(config) : undefined;
            badge.textContent = openBadgeLabel(
                status,
                badgeState.minutesLate,
                badgeState.minutesEarly,
                badgeState.beforeOpen,
                badgeState.opensInLabel,
                badgeState.justMadeIt,
                pushups,
            );
        }
    }
    if (clockEl) {
        clockEl.textContent = openClockText(new Date(nowMs()));
        clockEl.className = `check-in-open-clock check-in-open-clock--${statusClass(status)}${status === 'late' ? ' check-in-open-clock--late' : ''}`;
    }
    if (mode === 'open') {
        syncLivePushups(config, status);
    } else {
        syncLivePushups(config, 'on_time');
    }
    card.classList.remove('check-in-hero__card--on-time', 'check-in-hero__card--late', 'check-in-hero__card--missed', 'check-in-hero__card--neutral');
    if (status === 'missed') {
        card.classList.add('check-in-hero__card--missed');
    } else if (status === 'late') {
        card.classList.add('check-in-hero__card--late');
    } else if (badgeState.beforeOpen) {
        card.classList.add('check-in-hero__card--neutral');
    } else {
        card.classList.add('check-in-hero__card--on-time');
    }
    syncLiveIcon(status, mode);
}

function buildMetaHtml(config: GoalsCheckInConfig): string {
    const meta = config.meetingMeta;
    if (!meta?.label) return '';
    return `<p class="check-in-hero__meta">${escHtml(meta.label)}</p>`;
}

function syncMeetingLink(url?: string | null): void {
    const wrap = document.getElementById('check-in-meeting-link-wrap');
    if (!wrap) return;
    const href = (url || '').trim();
    if (!href) {
        wrap.innerHTML = '';
        wrap.hidden = true;
        return;
    }
    wrap.hidden = false;
    wrap.innerHTML = `<a href="${escHtml(href)}" class="check-in-meeting-badge">Meeting page</a>`;
}

function meetingUrlFromData(data: CheckInAjaxResponse['data'], config: GoalsCheckInConfig): string {
    return (data.meeting_url || config.meetingUrl || '').trim();
}

function resetCheckInStep(): void {
    checkInStep = 'idle';
}

function revealLateNoteStep(config: GoalsCheckInConfig, btn: HTMLButtonElement): void {
    checkInStep = 'late_note';
    const noteInput = document.getElementById('check-in-note') as HTMLInputElement | null;
    const noteEl = noteInput?.closest('.check-in-note') as HTMLLabelElement | null;
    if (noteEl && noteInput) {
        noteEl.hidden = false;
        noteEl.classList.remove('check-in-note--hidden');
        syncNoteFieldParsleyAttrs(noteInput, true);
        const form = noteEl.closest('.check-in-form');
        if (form) {
            initCheckInParsley(form);
        }
        noteInput.focus();
    }
    btn.disabled = false;
    btn.textContent = 'Submit';
    const card = btn.closest('.check-in-hero__card');
    if (card) {
        card.classList.remove('check-in-hero__card--neutral', 'check-in-hero__card--on-time');
        card.classList.add('check-in-hero__card--late');
    }
    syncLiveStatus(config, 'open');
}

function handleCheckInClick(config: GoalsCheckInConfig, btn: HTMLButtonElement): void {
    if (checkInStep === 'late_note') {
        void submitCheckIn(config, btn);
        return;
    }
    const isLate = config.previewMode ? currentPreview === 'open_late' || isLateWindow(config) : isLateWindow(config);
    if (isLate) {
        revealLateNoteStep(config, btn);
        return;
    }
    void submitCheckIn(config, btn);
}

function bindCelebrateButton(stage: HTMLElement): void {
    const btn = stage.querySelector('#check-in-celebrate-btn') as HTMLButtonElement | null;
    if (!btn || btn.dataset.bound === '1') return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
        fireOnTimeCelebrationBurst();
    });
}

function initExistingSuccess(config: GoalsCheckInConfig): void {
    if (!config.existing) return;
    const stage = document.getElementById('check-in-preview-stage');
    if (!stage) return;
    const success = stage.querySelector('.check-in-success') as HTMLElement | null;
    if (!success) return;
    success.classList.remove('check-in-success--revealed');
    void success.offsetWidth;
    revealResult(stage);
    fireSuccessEffect(config.existing.status || 'on_time', stage);
    if (config.existing.status === 'missed') {
        bindSentCheckinButton(config, config.existing);
    }
    if (config.existing.status === 'on_time') {
        bindCelebrateButton(stage);
    }
    syncMeetingLink(config.meetingUrl);
}

function initOpenStatus(config?: GoalsCheckInConfig): void {
    const clockEl = document.getElementById('check-in-open-clock');
    const badgeEl = document.getElementById('check-in-live-status');
    if (!config || (!clockEl && !badgeEl)) return;
    const tick = () => {
        const mode = document.getElementById('check-in-missed-btn') ? 'missed' : 'open';
        syncLiveStatus(config, mode);
        if (mode === 'open') {
            syncNoteVisibility(config);
        }
    };
    tick();
    if (clockInterval) clearInterval(clockInterval);
    clockInterval = setInterval(tick, 1000);
    if (visibilityTick) {
        document.removeEventListener('visibilitychange', visibilityTick);
    }
    visibilityTick = () => {
        if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', visibilityTick);
}

function buildSuccessBodyHtml(data: CheckInAjaxResponse['data'], config: GoalsCheckInConfig): string {
    const status = data.status || 'on_time';
    const note = (data.note || '').trim();
    const timestamp = data.timestamp || data.time || '';
    if (status === 'on_time') {
        return `
            <h2 class="check-in-success__headline">${escHtml(headlineForStatus(status, undefined, undefined, config.userName))}</h2>
            <p class="check-in-success__time">Checked in at ${escHtml(timestamp)}</p>
            ${note ? `<p class="check-in-success__note">${escHtml(note)}</p>` : ''}
            <button type="button" id="check-in-celebrate-btn" class="btn btn-primary btn-block btn-lg check-in-hero__btn check-in-celebrate-btn" aria-label="Replay celebration">&#10003; Checked in</button>`;
    }
    if (status === 'missed') {
        const sent = !!data.checkin_sent;
        const reminderClass = sent ? '' : ' check-in-success__headline--reminder';
        return `
            <h2 class="check-in-success__headline${reminderClass}">${escHtml(missedSuccessHeadline(sent))}</h2>
            ${sent ? '' : sentCheckinButtonHtml(config)}`;
    }
    return `
            <p class="check-in-success__welcome check-in-success__replay" role="button" tabindex="0" aria-label="Replay celebration">${escHtml(headlineForStatus(status, undefined, undefined, config.userName))}</p>
            <p class="check-in-success__meta">Checked in at ${escHtml(timestamp)}</p>`;
}

function buildSuccessInnerHtml(data: CheckInAjaxResponse['data'], config: GoalsCheckInConfig, revealed = false): string {
    const status = data.status || 'on_time';
    const revealedClass = revealed ? ' check-in-success--revealed' : '';
    return `
        <div class="check-in-success check-in-success--${escHtml(statusClass(status))} check-in-success--in-card${revealedClass}">
            ${buildSuccessBodyHtml(data, config)}
        </div>`;
}

function buildSuccessCardHtml(config: GoalsCheckInConfig, data: CheckInAjaxResponse['data'], revealed = false): string {
    const status = data.status || 'on_time';
    const variant = cardVariantForStatus(status);
    const isLate = normalizeStatus(status) === 'late';
    const isMissed = status === 'missed';
    const note = (data.note || '').trim();
    const minute = data.minute ?? 0;
    const pushups = data.pushups ?? 0;
    const missedSeasonCount = data.missedSeasonCount ?? config.missedSeasonCount ?? 0;
    const minutesEarly = data.minutes_early ?? 0;
    const justMadeIt = data.just_made_it ?? false;
    return `
        <div class="check-in-hero">
            <div class="check-in-hero__card ${cardClassForVariant(variant)}">
                <div class="check-in-hero__header">
                    <div class="check-in-hero__identity">
                        ${isMissed ? iconForMissed(data) : iconForVariant(variant)}
                        ${buildTitleHtml(status)}
                        ${buildMetaHtml(config)}
                    </div>
                    ${status === 'on_time' ? buildOnTimeSuccessLiveHtml(minutesEarly, justMadeIt) : ''}
                    ${isLate ? buildLateSuccessLiveHtml(status, minute, note, pushups) : ''}
                    ${isMissed ? buildMissedSuccessLiveHtml(note, missedSeasonCount) : ''}
                </div>
                ${buildSuccessInnerHtml(data, config, revealed)}
                ${cardLoadOverlayHtml()}
            </div>
        </div>`;
}

function buildOpenHtml(config: GoalsCheckInConfig, variant: 'on_time' | 'late'): string {
    const isLate = variant === 'late';
    const liveStatus = isLate ? 'late' : 'on_time';
    const titleStatus = isLate ? 'late' : 'on_time';
    const cardClass = isLate ? cardClassForVariant('late') : isBeforeCheckinOpen(config) ? 'check-in-hero__card--neutral' : cardClassForVariant('on_time');
    const badgeState = isLate
        ? { minutesLate: 15, minutesEarly: 0, justMadeIt: false, beforeOpen: false, opensInLabel: '' }
        : liveOpenBadgeState(config, 'on_time');
    const titleText = isLate ? heroTitleForStatus('late') : heroTitleForLiveOpen(config, 'on_time', badgeState.beforeOpen);
    return `
        <div class="check-in-hero">
            <div class="check-in-hero__card ${cardClass}">
                <form class="check-in-form" data-parsley-validate novalidate>
                <div class="check-in-hero__header">
                    ${buildIdentityHtml(config, pendingIconForStatus(liveStatus), titleStatus, titleText)}
                    ${buildLiveHtml(liveStatus, true, badgeState.minutesLate, 0, badgeState.minutesEarly, badgeState.beforeOpen, badgeState.opensInLabel, badgeState.justMadeIt)}
                </div>
                <label class="check-in-note check-in-note--hidden" for="check-in-note" hidden>
                    <span class="check-in-note__label">Reason for being late:</span>
                    <input type="text" id="check-in-note" name="checkin_note" maxlength="500" ${LATE_NOTE_INPUT_ATTRS} aria-label="Reason for being late:">
                </label>
                <button type="button" id="check-in-btn" class="btn btn-primary btn-block btn-lg check-in-hero__btn">Check In</button>
                </form>
                ${cardLoadOverlayHtml()}
            </div>
        </div>
        <div id="check-in-result-wrap" hidden></div>`;
}

function formatCloseTimeLabel(closeAt: string): string {
    const dt = new Date(closeAt);
    if (Number.isNaN(dt.getTime())) return '';
    return (
        dt.toLocaleString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/New_York',
        }) + ' ET'
    );
}

function buildClosedHtml(config: GoalsCheckInConfig): string {
    const closeLabel = formatCloseTimeLabel(config.closeAt);
    const meta = closeLabel ? `<p class="check-in-hero__meta">Today's check-in window ended at ${escHtml(closeLabel)}.</p>` : '';
    return `
        <div class="check-in-hero">
            <div class="check-in-hero__card check-in-hero__card--neutral">
                <div class="check-in-hero__header">
                    <div class="check-in-hero__identity">
                        ${pendingIconForStatus('on_time')}
                        <h2 class="check-in-hero__title">Check-in closed</h2>
                        ${meta}
                    </div>
                </div>
                ${cardLoadOverlayHtml()}
            </div>
        </div>`;
}

function buildMissedReportHtml(config: GoalsCheckInConfig): string {
    return `
        <div class="check-in-hero">
            <div class="check-in-hero__card check-in-hero__card--missed">
                <form class="check-in-form" data-parsley-validate novalidate>
                <div class="check-in-hero__header">
                    ${buildIdentityHtml(config, MISSED_ICON, 'missed')}
                    ${buildLiveHtml('missed', false, 0, config.missedSeasonCount ?? 1)}
                </div>
                <label class="check-in-note check-in-note--required" for="check-in-missed-note">
                    <span class="check-in-note__label">Reason for missing:</span>
                    <input type="text" id="check-in-missed-note" name="checkin_missed_note" maxlength="500" ${MISSED_NOTE_INPUT_ATTRS} aria-label="Reason for missing:">
                </label>
                <button type="button" id="check-in-missed-btn" class="btn btn-primary btn-block btn-lg check-in-hero__btn">Submit</button>
                </form>
                ${cardLoadOverlayHtml()}
            </div>
        </div>`;
}

function syncNoteFieldParsleyAttrs(noteInput: HTMLInputElement, required: boolean): void {
    if (required) {
        noteInput.setAttribute('required', '');
        noteInput.setAttribute('data-parsley-required', 'true');
        noteInput.removeAttribute('data-parsley-excluded');
    } else {
        noteInput.removeAttribute('required');
        noteInput.removeAttribute('data-parsley-required');
        noteInput.setAttribute('data-parsley-excluded', 'true');
    }
}

function syncNoteVisibility(config: GoalsCheckInConfig): void {
    const noteInput = document.getElementById('check-in-note') as HTMLInputElement | null;
    const noteEl = noteInput?.closest('.check-in-note') as HTMLLabelElement | null;
    if (!noteEl || !noteInput) return;
    const show = checkInStep === 'late_note';
    const wasShown = !noteEl.hidden && !noteEl.classList.contains('check-in-note--hidden');
    if (show) {
        noteEl.hidden = false;
        noteEl.classList.remove('check-in-note--hidden');
        syncNoteFieldParsleyAttrs(noteInput, true);
    } else {
        noteEl.hidden = true;
        noteEl.classList.add('check-in-note--hidden');
        syncNoteFieldParsleyAttrs(noteInput, false);
    }
    if (show !== wasShown) {
        const form = noteEl.closest('.check-in-form');
        if (form) {
            initCheckInParsley(form);
        }
    }
}

function clearIntervals(): void {
    if (clockInterval) {
        clearInterval(clockInterval);
        clockInterval = null;
    }
    if (syncInterval) {
        clearInterval(syncInterval);
        syncInterval = null;
    }
    if (visibilityTick) {
        document.removeEventListener('visibilitychange', visibilityTick);
        visibilityTick = null;
    }
    clearWindowStatePoll();
}

function clearWindowStatePoll(): void {
    if (windowStatePollInterval) {
        clearInterval(windowStatePollInterval);
        windowStatePollInterval = null;
    }
    windowStatePollConfig = null;
}

function initClock(config?: GoalsCheckInConfig): void {
    initOpenStatus(config);
}

function bindOpenView(config: GoalsCheckInConfig): void {
    initClock(config);
    syncNoteVisibility(config);
    initCheckInParsley();
    bindCheckInNoteValidation();
    const btn = document.getElementById('check-in-btn') as HTMLButtonElement | null;
    if (!btn) return;
    const syncButton = () => {
        syncLiveStatus(config, 'open');
        syncNoteVisibility(config);
        if (config.bypassWindow) {
            btn.disabled = false;
            btn.textContent = 'Check In';
            return;
        }
        const closeAt = new Date(config.closeAt).getTime();
        const now = nowMs();
        if (config.state === 'open') {
            btn.disabled = now >= closeAt;
        } else {
            const openAt = new Date(config.openAt).getTime();
            btn.disabled = now < openAt || now >= closeAt;
        }
        const waitingLabel = opensInLabel(config);
        btn.textContent =
            checkInStep === 'late_note' ? 'Submit' : btn.disabled && waitingLabel ? waitingLabel : 'Check In';
    };
    syncButton();
    if (syncInterval) clearInterval(syncInterval);
    syncInterval = setInterval(syncButton, 1000);
    btn.replaceWith(btn.cloneNode(true));
    const freshBtn = document.getElementById('check-in-btn') as HTMLButtonElement;
    freshBtn.addEventListener('click', () => {
        handleCheckInClick(config, freshBtn);
    });
}

function bindMissedView(config: GoalsCheckInConfig): void {
    const btn = document.getElementById('check-in-missed-btn') as HTMLButtonElement | null;
    const form = getCheckInForm();
    if (!btn) return;
    btn.replaceWith(btn.cloneNode(true));
    const freshBtn = document.getElementById('check-in-missed-btn') as HTMLButtonElement;
    freshBtn.addEventListener('click', () => {
        void submitMissedReport(config, freshBtn);
    });
    if (form && form.dataset.checkInMissedBound !== '1') {
        form.dataset.checkInMissedBound = '1';
        form.addEventListener('submit', e => {
            e.preventDefault();
            void submitMissedReport(config, freshBtn);
        });
    }
    initClock(config);
    syncLiveStatus(config, 'missed');
    initCheckInParsley();
    bindCheckInNoteValidation();
}

function setActivePreviewButton(preview: PreviewMode): void {
    document.querySelectorAll('.check-in-preview__btn').forEach(el => {
        el.classList.toggle('check-in-preview__btn--active', el.getAttribute('data-preview') === preview);
    });
}

function getDefaultPreview(config: GoalsCheckInConfig): PreviewMode {
    if (config.existing) {
        const status = config.existing.status;
        if (status === 'on_time') return 'success_on_time';
        if (status === 'missed') return 'success_missed';
        return 'success_late';
    }
    if (config.state === 'missed_report') return 'missed_report';
    if (config.state === 'open') return 'open_on_time';
    return 'open_on_time';
}

function previewSuccessData(preview: PreviewMode, config: GoalsCheckInConfig): CheckInAjaxResponse['data'] | null {
    if (preview === 'success_on_time') {
        return { status: 'on_time', pushups: 0, timestamp: formatMockTimestamp(), note: '', minutes_early: 25, just_made_it: false };
    }
    if (preview === 'success_late') {
        return {
            status: 'late',
            pushups: 20,
            minute: 15,
            timestamp: formatMockTimestamp(),
            checkedInAt: formatMockFullTimestamp(),
            note: 'Traffic on the way in.',
        };
    }
    if (preview === 'success_missed') {
        const now = new Date();
        return {
            status: 'missed',
            pushups: 0,
            timestamp: formatMockReportedTimestamp(),
            note: 'Out of town for work.',
            checkin_sent: false,
            checkInAt: now.toISOString(),
            sentConfirmAvailableAt: new Date(now.getTime() + sentConfirmDelayMs(config)).toISOString(),
            meeting_date: config.meetingDate || config.reportMeetingDate || undefined,
        };
    }
    return null;
}

function previewModeForStatus(status: string): PreviewMode {
    if (status === 'on_time') return 'success_on_time';
    if (status === 'missed') return 'success_missed';
    return 'success_late';
}

function showSuccessResult(config: GoalsCheckInConfig, data: CheckInAjaxResponse['data'], options: { preview?: PreviewMode; animate?: boolean } = {}): void {
    const stage = document.getElementById('check-in-preview-stage');
    if (!stage) return;
    const animate = options.animate !== false;
    resetCheckInStep();
    resetSuccessEffects();
    stage.innerHTML = buildSuccessCardHtml(config, data, !animate);
    markCheckInCardsReady(stage);
    if (animate) {
        revealResult(stage);
    }
    fireSuccessEffect(data.status || 'on_time', stage);
    syncMeetingLink(meetingUrlFromData(data, config));
    if ((data.status || 'on_time') === 'on_time') {
        bindCelebrateButton(stage);
    }
    if (options.preview && config.previewMode) {
        setPreviewUrl(options.preview);
        setActivePreviewButton(options.preview);
    }
    if ((data.status || 'on_time') === 'missed') {
        bindSentCheckinButton(config, data);
    }
}

function renderPreview(config: GoalsCheckInConfig, preview: PreviewMode, updateUrl = true): void {
    const stage = document.getElementById('check-in-preview-stage');
    if (!stage) return;
    clearIntervals();
    resetSuccessEffects();
    currentPreview = preview;
    const messages = document.getElementById('check-in-messages');
    if (messages) messages.innerHTML = '';
    if (updateUrl) {
        setPreviewUrl(preview);
    }
    if (preview === 'open_on_time') {
        resetCheckInStep();
        noteForce = 'hide';
        stage.innerHTML = buildOpenHtml(config, 'on_time');
        bindOpenView(config);
    } else if (preview === 'open_late') {
        resetCheckInStep();
        noteForce = null;
        stage.innerHTML = buildOpenHtml(config, 'on_time');
        bindOpenView(config);
    } else if (preview === 'missed_report') {
        noteForce = null;
        stage.innerHTML = buildMissedReportHtml(config);
        bindMissedView(config);
    } else {
        noteForce = null;
        const successData = previewSuccessData(preview, config);
        if (successData) {
            showSuccessResult(config, successData, { preview, animate: true });
        }
    }
    setActivePreviewButton(preview);
}

function initPreviewControls(config: GoalsCheckInConfig): void {
    const controls = document.getElementById('check-in-preview-controls');
    if (!controls) return;
    const urlPreview = getPreviewFromUrl();
    if (urlPreview) {
        renderPreview(config, urlPreview, false);
    } else {
        renderPreview(config, getDefaultPreview(config), false);
    }
    controls.querySelectorAll('.check-in-preview__btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const preview = btn.getAttribute('data-preview');
            if (isValidPreview(preview)) {
                renderPreview(config, preview);
            }
        });
    });
}

function resetSuccessEffects(): void {
    confetti.reset();
    document.querySelectorAll('.check-in-hero__card--effect-late, .check-in-hero__card--effect-missed').forEach(el => {
        el.classList.remove('check-in-hero__card--effect-late', 'check-in-hero__card--effect-missed');
    });
}

function pulseSuccessCard(stage: HTMLElement, effectClass: 'check-in-hero__card--effect-late' | 'check-in-hero__card--effect-missed'): void {
    const card = stage.querySelector('.check-in-hero__card');
    if (!card) return;
    card.classList.add(effectClass);
    window.setTimeout(() => {
        card.classList.remove(effectClass);
    }, 900);
}

function fireOnTimeCelebrationBurst(): void {
    if (prefersReducedMotion()) return;
    const colors = ['#22c55e', '#6ee7b7', '#ffffff', '#add8e6', '#082b51'];
    confetti({
        particleCount: 25,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.65 },
        colors,
    });
    confetti({
        particleCount: 25,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.65 },
        colors,
    });
}

function fireOnTimeCelebration(): void {
    if (prefersReducedMotion()) return;
    const colors = ['#22c55e', '#6ee7b7', '#ffffff', '#add8e6', '#082b51'];
    const duration = 400;
    const end = Date.now() + duration;
    const frame = () => {
        confetti({
            particleCount: 2,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.65 },
            colors,
        });
        confetti({
            particleCount: 2,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.65 },
            colors,
        });
        if (Date.now() < end) {
            requestAnimationFrame(frame);
        }
    };
    frame();
}

function fireLateAcknowledgment(stage: HTMLElement): void {
    const colors = ['#eab308', '#fcd34d', '#f59e0b', '#fef08a'];
    confetti({
        particleCount: 55,
        spread: 78,
        origin: { x: 0.5, y: 0.58 },
        colors,
        ticks: 110,
        gravity: 1.15,
        scalar: 0.95,
        startVelocity: 28,
    });
    window.setTimeout(() => {
        confetti({
            particleCount: 18,
            angle: 75,
            spread: 38,
            origin: { x: 0.35, y: 0.72 },
            colors,
            ticks: 90,
        });
        confetti({
            particleCount: 18,
            angle: 105,
            spread: 38,
            origin: { x: 0.65, y: 0.72 },
            colors,
            ticks: 90,
        });
    }, 180);
    pulseSuccessCard(stage, 'check-in-hero__card--effect-late');
}

function fireMissedAcknowledgment(stage: HTMLElement): void {
    const colors = ['#ef4444', '#991b1b', '#64748b', '#475569', '#fca5a5'];
    confetti({
        particleCount: 36,
        spread: 360,
        origin: { x: 0.5, y: 0.15 },
        colors,
        ticks: 220,
        gravity: 0.55,
        drift: 0,
        scalar: 0.65,
        startVelocity: 12,
    });
    pulseSuccessCard(stage, 'check-in-hero__card--effect-missed');
}

function fireSuccessEffect(status: string, stage?: HTMLElement | null): void {
    if (prefersReducedMotion()) return;
    const host = stage || document.getElementById('check-in-preview-stage');
    if (status === 'on_time') {
        fireOnTimeCelebration();
        return;
    }
    if (normalizeStatus(status) === 'late') {
        if (host) fireLateAcknowledgment(host);
        return;
    }
    if (status === 'missed' && host) {
        fireMissedAcknowledgment(host);
    }
}

function statusFromSuccessElement(el: Element): string {
    const match = el.className.match(/check-in-success--(on-time|late|missed)/);
    if (!match) return 'on_time';
    if (match[1] === 'on-time') return 'on_time';
    return match[1];
}

function replaySuccessAnimation(stage: HTMLElement, status: string): void {
    const success = stage.querySelector('.check-in-success') as HTMLElement | null;
    if (!success) return;
    resetSuccessEffects();
    success.classList.remove('check-in-success--revealed');
    void success.offsetWidth;
    revealResult(stage);
    fireSuccessEffect(status, stage);
}

function findReplayTarget(el: EventTarget | null): HTMLElement | null {
    if (!(el instanceof HTMLElement)) return null;
    if (el.id === 'check-in-sent-btn' || el.closest('#check-in-sent-btn')) return null;
    const replay = el.closest('.check-in-success__replay');
    if (!(replay instanceof HTMLElement)) return null;
    const card = replay.closest('.check-in-hero__card');
    if (!card?.querySelector('.check-in-success')) return null;
    if (card.querySelector('#check-in-missed-btn')) return null;
    return replay;
}

function initSuccessReplay(): void {
    const stage = document.getElementById('check-in-preview-stage');
    if (!stage || stage.dataset.replayBound === '1') return;
    stage.dataset.replayBound = '1';
    const triggerReplay = (target: HTMLElement) => {
        const card = target.closest('.check-in-hero__card');
        const success = card?.querySelector('.check-in-success');
        if (!card || !success) return;
        replaySuccessAnimation(stage, statusFromSuccessElement(success));
    };
    stage.addEventListener('click', e => {
        const target = findReplayTarget(e.target);
        if (!target) return;
        triggerReplay(target);
    });
    stage.addEventListener('keydown', e => {
        const target = findReplayTarget(e.target);
        if (!target) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        triggerReplay(target);
    });
}

function revealResult(container: HTMLElement): void {
    const card = container.querySelector('.check-in-success');
    if (!card) return;
    requestAnimationFrame(() => {
        card.classList.add('check-in-success--revealed');
    });
}

function showError(msg: string): void {
    const el = document.getElementById('check-in-messages');
    if (el) el.innerHTML = `<p class="error">${escHtml(msg)}</p>`;
}

function simulatedCheckInStatus(config: GoalsCheckInConfig): 'on_time' | 'late' {
    if (checkInStep === 'late_note') {
        return 'late';
    }
    if (currentPreview === 'open_on_time' || noteForce === 'hide') {
        return 'on_time';
    }
    return isLateWindow(config) ? 'late' : 'on_time';
}

function existingFromCheckInResponse(data: CheckInAjaxResponse['data']): GoalsCheckInConfig['existing'] {
    if (!data?.status) {
        return null;
    }
    return {
        status: data.status,
        pushups: data.pushups ?? 0,
        minute: data.minute,
        minutes_early: data.minutes_early,
        in_grace: data.in_grace,
        just_made_it: data.just_made_it,
        time: data.time ?? '',
        timestamp: data.timestamp ?? '',
        message: data.message ?? '',
        note: data.note,
        checkin_sent: data.checkin_sent,
        checkin_sent_at: data.checkin_sent_at,
        checkInAt: data.checkInAt,
        sentConfirmAvailableAt: data.sentConfirmAvailableAt,
        meeting_date: data.meeting_date,
    };
}

function markCheckInComplete(config: GoalsCheckInConfig, data: CheckInAjaxResponse['data']): void {
    config.state = 'checked_in';
    config.existing = existingFromCheckInResponse(data);
    (window as Window & { goalsCheckIn?: GoalsCheckInConfig }).goalsCheckIn = config;
    clearWindowStatePoll();
    initLiveBoardPolling(config);
}

function stageHasSuccessView(stage: HTMLElement | null): boolean {
    return !!stage?.querySelector('.check-in-success');
}

async function submitCheckIn(config: GoalsCheckInConfig, btn: HTMLButtonElement): Promise<void> {
    if (!validateCheckInForm()) {
        return;
    }
    const origHtml = btn.innerHTML;
    const noteEl = document.getElementById('check-in-note') as HTMLInputElement | null;
    const note = noteEl?.value.trim() || '';
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span><span>Checking in…</span>';
    if (config.previewMode) {
        clearIntervals();
        const status = simulatedCheckInStatus(config);
        noteForce = null;
        const preview = previewModeForStatus(status);
        currentPreview = preview;
        showSuccessResult(
            config,
            {
                status,
                pushups: status === 'late' ? 20 : 0,
                minute: status === 'late' ? 15 : 0,
                timestamp: formatMockTimestamp(),
                note,
            },
            { preview, animate: true },
        );
        const messages = document.getElementById('check-in-messages');
        if (messages) messages.innerHTML = '';
        return;
    }
    const formData = new FormData();
    formData.set('action', 'goals_check_in');
    formData.set('_wpnonce', config.nonce);
    if (note) {
        formData.set('note', note);
    }
    try {
        const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
        const data = (await res.json()) as CheckInAjaxResponse;
        if (!data.success) {
            showError(data.data?.message || 'Something went wrong. Please try again.');
            btn.disabled = false;
            btn.innerHTML = origHtml;
            return;
        }
        clearIntervals();
        noteForce = null;
        markCheckInComplete(config, data.data);
        showSuccessResult(config, data.data, { animate: true });
        const messages = document.getElementById('check-in-messages');
        if (messages) messages.innerHTML = '';
        void refreshLiveBoard(config);
    } catch {
        showError('Something went wrong. Please try again.');
        btn.disabled = false;
        btn.innerHTML = origHtml;
    }
}

async function submitSentCheckin(config: GoalsCheckInConfig, btn: HTMLButtonElement, data: CheckInAjaxResponse['data']): Promise<void> {
    if (btn.disabled) return;
    const origHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span><span>Saving…</span>';
    if (config.previewMode) {
        updateMissedSuccessCard(config, { ...data, checkin_sent: true });
        const messages = document.getElementById('check-in-messages');
        if (messages) messages.innerHTML = '';
        return;
    }
    const formData = new FormData();
    formData.set('action', 'goals_confirm_sent_checkin');
    formData.set('_wpnonce', config.nonce);
    formData.set('meeting_date', data.meeting_date || config.meetingDate || config.reportMeetingDate || '');
    try {
        const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
        const response = (await res.json()) as CheckInAjaxResponse;
        if (!response.success) {
            showError(response.data?.message || 'Something went wrong. Please try again.');
            btn.disabled = false;
            btn.innerHTML = origHtml;
            return;
        }
        updateMissedSuccessCard(config, response.data);
        const messages = document.getElementById('check-in-messages');
        if (messages) messages.innerHTML = '';
    } catch {
        showError('Something went wrong. Please try again.');
        btn.disabled = false;
        btn.innerHTML = origHtml;
    }
}

function updateMissedSuccessCard(config: GoalsCheckInConfig, data: CheckInAjaxResponse['data']): void {
    const stage = document.getElementById('check-in-preview-stage');
    if (!stage) return;
    clearSentCheckinInterval();
    stage.innerHTML = buildSuccessCardHtml(config, data, true);
    markCheckInCardsReady(stage);
    if (data.checkin_sent) {
        return;
    }
    bindSentCheckinButton(config, data);
}

function bindSentCheckinButton(config: GoalsCheckInConfig, data: CheckInAjaxResponse['data']): void {
    const btn = document.getElementById('check-in-sent-btn') as HTMLButtonElement | null;
    if (!btn || data.checkin_sent) return;
    btn.replaceWith(btn.cloneNode(true));
    const freshBtn = document.getElementById('check-in-sent-btn') as HTMLButtonElement;
    freshBtn.addEventListener('click', () => {
        if (freshBtn.disabled) return;
        void submitSentCheckin(config, freshBtn, data);
    });
    initSentCheckinCooldown(config, data);
}

async function submitMissedReport(config: GoalsCheckInConfig, btn: HTMLButtonElement): Promise<void> {
    const origHtml = btn.innerHTML;
    const noteEl = document.getElementById('check-in-missed-note') as HTMLInputElement | null;
    const note = noteEl?.value.trim() || '';
    if (!validateCheckInForm()) {
        return;
    }
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span><span>Submitting…</span>';
    if (config.previewMode) {
        clearIntervals();
        noteForce = null;
        currentPreview = 'success_missed';
        showSuccessResult(
            config,
            {
                status: 'missed',
                pushups: 0,
                timestamp: formatMockReportedTimestamp(),
                note,
                checkin_sent: false,
                checkInAt: new Date().toISOString(),
                sentConfirmAvailableAt: new Date(Date.now() + sentConfirmDelayMs(config)).toISOString(),
                meeting_date: config.meetingDate || config.reportMeetingDate || undefined,
            },
            { preview: 'success_missed', animate: true },
        );
        const messages = document.getElementById('check-in-messages');
        if (messages) messages.innerHTML = '';
        return;
    }
    const formData = new FormData();
    formData.set('action', 'goals_report_missed');
    formData.set('_wpnonce', config.nonce);
    formData.set('note', note);
    try {
        const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
        const data = (await res.json()) as CheckInAjaxResponse;
        if (!data.success) {
            showError(data.data?.message || 'Something went wrong. Please try again.');
            btn.disabled = false;
            btn.innerHTML = origHtml;
            return;
        }
        clearIntervals();
        noteForce = null;
        markCheckInComplete(config, data.data);
        showSuccessResult(config, data.data, { animate: true });
        const messages = document.getElementById('check-in-messages');
        if (messages) messages.innerHTML = '';
        void refreshLiveBoard(config);
    } catch {
        showError('Something went wrong. Please try again.');
        btn.disabled = false;
        btn.innerHTML = origHtml;
    }
}

const ON_TIME_ATTENDANCE_ICON =
    '<svg class="attendance-icon attendance-icon--on-time" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>';

const LATE_ATTENDANCE_ICON =
    '<svg class="attendance-icon attendance-icon--late" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>';

const MISSED_ATTENDANCE_ICON =
    '<svg class="attendance-icon attendance-icon--missed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>';

const MISSED_SENT_ATTENDANCE_ICON =
    '<svg class="attendance-icon attendance-icon--missed-sent" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>';

const WAITING_ATTENDANCE_ICON =
    '<svg class="attendance-icon attendance-icon--waiting" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>';

interface LiveBoardRow {
    user_id: number;
    name: string;
    status: string;
    status_label: string;
    time?: string;
    checked_in_at?: string;
    checkin_sent?: boolean;
    just_made_it?: boolean;
    pushups?: number;
}

interface LiveBoardData {
    meeting_date: string;
    members: LiveBoardRow[];
    counts: {
        on_time: number;
        late: number;
        missed: number;
        waiting: number;
        checked_in: number;
        roster: number;
    };
    progress_mask?: number;
    phase?: string;
    meta_label?: string;
}

interface LiveBoardResponse {
    success: boolean;
    data: LiveBoardData & { message?: string };
}

let liveBoardPollInterval: ReturnType<typeof setInterval> | null = null;
let liveBoardKnownCheckedIn = new Set<number>();
let liveBoardConfig: GoalsCheckInConfig | null = null;
let liveBoardAfterCloseDone = false;

function clearLiveBoardPoll(): void {
    if (liveBoardPollInterval) {
        clearInterval(liveBoardPollInterval);
        liveBoardPollInterval = null;
    }
}

function shouldPollWindowState(config: GoalsCheckInConfig): boolean {
    if (config.previewMode || config.bypassWindow) {
        return false;
    }
    if (config.followsCurrent) {
        return true;
    }
    return config.state === 'before_open' || config.state === 'missed_report' || config.state === 'open';
}

function applyWindowStateFromPoll(config: GoalsCheckInConfig, data: WindowStatePollResponse): boolean {
    syncServerClock(data.serverNow);
    const localCheckedIn = config.state === 'checked_in' && !!config.existing;
    if (localCheckedIn && data.state === 'open' && !data.existing) {
        data = { ...data, state: 'checked_in', existing: config.existing };
    }
    config.state = data.state;
    config.meetingDate = data.meetingDate;
    config.reportMeetingDate = data.reportMeetingDate ?? null;
    config.canReportMissed = !!data.canReportMissed;
    config.openAt = data.openAt;
    config.checkinOpenAt = data.checkinOpenAt ?? data.openAt;
    config.closeAt = data.closeAt;
    config.meetingStartAt = data.meetingStartAt;
    if (data.nextMeetingDate) {
        config.nextMeetingDate = data.nextMeetingDate;
    }
    if (data.liveBoardPhase) {
        config.liveBoardPhase = data.liveBoardPhase;
    }
    if (data.existing) {
        config.existing = data.existing;
    } else if (!localCheckedIn) {
        config.existing = data.existing ?? null;
    }
    config.bypassWindow = !!data.bypassWindow;
    (window as Window & { goalsCheckIn?: GoalsCheckInConfig }).goalsCheckIn = config;

    if (data.state === 'checked_in' && data.existing) {
        clearWindowStatePoll();
        const stage = document.getElementById('check-in-preview-stage');
        if (stage) {
            clearIntervals();
            showSuccessResult(config, data.existing, { animate: true });
        }
        initLiveBoardPolling(config);
        return true;
    }

    if (data.state === 'after_close') {
        clearWindowStatePoll();
        const stage = document.getElementById('check-in-preview-stage');
        if (stage && !config.existing && !stageHasSuccessView(stage)) {
            clearIntervals();
            stage.innerHTML = buildClosedHtml(config);
        }
        liveBoardAfterCloseDone = false;
        void refreshLiveBoard(config).then(() => {
            liveBoardAfterCloseDone = true;
            clearLiveBoardPoll();
        });
        return true;
    }

    if (data.state === 'open') {
        const stage = document.getElementById('check-in-preview-stage');
        if (config.existing || data.existing || stageHasSuccessView(stage)) {
            initLiveBoardPolling(config);
            return false;
        }
        if (!document.getElementById('check-in-btn') && stage) {
            clearIntervals();
            resetCheckInStep();
            noteForce = null;
            stage.innerHTML = buildOpenHtml(config, 'on_time');
            bindOpenView(config);
            initLiveBoardPolling(config);
            return true;
        }
        return false;
    }

    return false;
}

async function fetchWindowState(config: GoalsCheckInConfig): Promise<WindowStatePollResponse | null> {
    const formData = new FormData();
    formData.set('action', 'goals_checkin_window_state');
    formData.set('_wpnonce', config.nonce);
    try {
        const res = await fetch(config.ajaxUrl, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });
        const json = (await res.json()) as { success: boolean; data?: WindowStatePollResponse };
        if (!json.success || !json.data) {
            return null;
        }
        return json.data;
    } catch {
        return null;
    }
}

async function refreshWindowState(config: GoalsCheckInConfig): Promise<void> {
    const data = await fetchWindowState(config);
    if (!data) {
        return;
    }
    const pageDate = (config.meetingDate || '').trim();
    const nextDate = (data.meetingDate || '').trim();
    if (pageDate && nextDate && pageDate !== nextDate) {
        if (config.followsCurrent) {
            window.location.reload();
            return;
        }
        clearWindowStatePoll();
        return;
    }
    if (applyWindowStateFromPoll(config, data)) {
        return;
    }
    syncServerClock(data.serverNow);
    if (data.liveBoardPhase) {
        config.liveBoardPhase = data.liveBoardPhase;
    }
    if (config.state === 'before_open') {
        initOpenStatus(config);
    } else if (config.state === 'missed_report') {
        syncLiveStatus(config, 'missed');
    }
}

function initWindowStatePolling(config: GoalsCheckInConfig): void {
    clearWindowStatePoll();
    if (!shouldPollWindowState(config)) {
        return;
    }
    windowStatePollConfig = config;
    const pollMs = config.livePollMs ?? 5000;
    const tick = () => {
        if (!windowStatePollConfig) {
            return;
        }
        void refreshWindowState(windowStatePollConfig);
    };
    void refreshWindowState(config);
    windowStatePollInterval = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && windowStatePollConfig) {
            void refreshWindowState(windowStatePollConfig);
        }
    });
}

function liveBoardMeetingDateForPoll(config: GoalsCheckInConfig): string | null | undefined {
    if (config.state === 'checked_in' && config.existing?.meeting_date) {
        return config.existing.meeting_date;
    }
    return config.liveBoardMeetingDate;
}

function shouldPollLiveBoard(config: GoalsCheckInConfig): boolean {
    if (!config.liveBoardEnabled || !liveBoardMeetingDateForPoll(config)) {
        return false;
    }
    if (config.liveBoardPoll === false) {
        return false;
    }
    if (config.bypassWindow || config.liveBoardPoll === true) {
        return true;
    }
    const phase = config.liveBoardPhase;
    if (phase === 'after_close') {
        return !liveBoardAfterCloseDone;
    }
    if (phase === 'before_open' || phase === 'open' || config.state === 'open' || config.state === 'checked_in' || config.state === 'before_open') {
        return true;
    }
    return false;
}

function liveBoardStatusIcon(status: string, checkinSent = false): string {
    if (status === 'waiting') return WAITING_ATTENDANCE_ICON;
    if (status === 'on_time') return ON_TIME_ATTENDANCE_ICON;
    if (status === 'missed') return checkinSent ? MISSED_SENT_ATTENDANCE_ICON : MISSED_ATTENDANCE_ICON;
    return LATE_ATTENDANCE_ICON;
}

function liveBoardRowMeta(row: LiveBoardRow): string {
    return row.status_label || '';
}

function liveBoardRowHtml(row: LiveBoardRow, isNew = false): string {
    const statusClassName = row.status.replace(/_/g, '-');
    const newClass = isNew ? ' check-in-live-board__row--new' : '';
    return `<li class="attendance-list__item check-in-live-board__row check-in-live-board__row--${escHtml(statusClassName)}${newClass}" data-user-id="${row.user_id}" data-status="${escHtml(row.status)}">
        <span class="attendance-list__status">
            ${liveBoardStatusIcon(row.status, !!row.checkin_sent)}
            <span class="attendance-list__name">${escHtml(row.name)}</span>
        </span>
        <span class="attendance-list__meta check-in-live-board__status">${escHtml(liveBoardRowMeta(row))}</span>
    </li>`;
}

function animateLiveBoardProgress(board: HTMLElement, mask: number): void {
    const bar = board.querySelector('.stats-v2-pulse-progress-bar') as HTMLElement | null;
    if (!bar) return;
    bar.style.width = '100%';
    const target = `${Math.max(0, Math.min(100, mask))}%`;
    if (prefersReducedMotion()) {
        bar.style.width = target;
        return;
    }
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            bar.style.width = target;
        });
    });
}

function renderLiveBoard(data: LiveBoardData, previousCheckedIn = liveBoardKnownCheckedIn): void {
    const board = document.getElementById('check-in-live-board');
    if (!board) return;
    const nextCheckedIn = new Set(data.members.filter(row => row.status !== 'waiting' && row.status !== 'missed').map(row => row.user_id));
    const countEl = board.querySelector('.check-in-live-board__count');
    if (countEl) {
        countEl.innerHTML = `<span class="stats-v2-pulse-count-num">${data.counts.checked_in}</span> of <span class="stats-v2-pulse-count-num">${data.counts.roster}</span> checked in`;
    }
    animateLiveBoardProgress(board, data.progress_mask ?? 100);
    const list = board.querySelector('.check-in-live-board__list');
    if (!list) return;
    const rows = data.members.map(row => {
        const isNew = row.status !== 'waiting' && row.status !== 'missed' && !previousCheckedIn.has(row.user_id);
        return liveBoardRowHtml(row, isNew);
    });
    list.innerHTML = rows.join('');
    if (!prefersReducedMotion()) {
        window.setTimeout(() => {
            list.querySelectorAll('.check-in-live-board__row--new').forEach(el => {
                el.classList.remove('check-in-live-board__row--new');
            });
        }, 1200);
    }
    liveBoardKnownCheckedIn = nextCheckedIn;
}

async function fetchLiveBoard(config: GoalsCheckInConfig): Promise<LiveBoardData | null> {
    const meetingDate = liveBoardMeetingDateForPoll(config);
    if (!config.liveBoardEnabled || !meetingDate) {
        return null;
    }
    const formData = new FormData();
    formData.set('action', 'goals_checkin_live_board');
    formData.set('_wpnonce', config.nonce);
    formData.set('meeting_date', meetingDate);
    if (config.liveBoardPreviewSamples) {
        formData.set('preview_samples', '1');
    }
    try {
        const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
        const payload = (await res.json()) as LiveBoardResponse;
        if (!payload.success || !payload.data) {
            return null;
        }
        return payload.data;
    } catch {
        return null;
    }
}

async function refreshLiveBoard(config: GoalsCheckInConfig): Promise<void> {
    const data = await fetchLiveBoard(config);
    if (data) {
        if (data.phase) {
            config.liveBoardPhase = data.phase;
        }
        renderLiveBoard(data);
        if (config.liveBoardPhase === 'after_close') {
            liveBoardAfterCloseDone = true;
            clearLiveBoardPoll();
        }
    }
}

function initLiveBoardPolling(config: GoalsCheckInConfig): void {
    clearLiveBoardPoll();
    if (!shouldPollLiveBoard(config)) {
        return;
    }
    const pollMs = config.livePollMs ?? 5000;
    const tick = () => {
        if (document.visibilityState !== 'visible') {
            return;
        }
        void refreshLiveBoard(config);
    };
    liveBoardPollInterval = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && shouldPollLiveBoard(config)) {
            void refreshLiveBoard(config);
        }
    });
}

function initLiveBoard(config: GoalsCheckInConfig): void {
    const board = document.getElementById('check-in-live-board');
    if (!board || !config.liveBoardEnabled) {
        return;
    }
    liveBoardConfig = config;
    const list = board.querySelector('.check-in-live-board__list');
    if (list) {
        list.querySelectorAll('.check-in-live-board__row').forEach(el => {
            const id = parseInt(el.getAttribute('data-user-id') || '', 10);
            const status = el.getAttribute('data-status') || '';
            if (!Number.isNaN(id) && status !== 'waiting' && status !== 'missed') {
                liveBoardKnownCheckedIn.add(id);
            }
        });
    }
    const bar = board.querySelector('.stats-v2-pulse-progress-bar') as HTMLElement | null;
    if (bar) {
        const mask = parseInt(bar.getAttribute('data-mask') || '100', 10);
        animateLiveBoardProgress(board, mask);
    }
    initLiveBoardPolling(config);
}

export function triggerLiveBoardRefresh(): void {
    if (liveBoardConfig) {
        void refreshLiveBoard(liveBoardConfig);
    }
}

export async function checkIn(): Promise<void> {
    if (!document.querySelector('.check-in')) return;
    const config = (window as Window & { goalsCheckIn?: GoalsCheckInConfig }).goalsCheckIn;
    if (!config) return;
    syncServerClock(config.serverNow);
    initSuccessReplay();
    const urlPreview = getPreviewFromUrl();
    const previewBlocksLive = !!(config.previewMode && urlPreview);
    await initCardLoadReveal();
    if (config.previewMode) {
        initPreviewControls(config);
    } else if (config.existing) {
        initExistingSuccess(config);
    } else if (!previewBlocksLive && document.getElementById('check-in-missed-btn')) {
        bindMissedView(config);
    } else if (!previewBlocksLive && document.getElementById('check-in-btn') && config.state === 'open') {
        resetCheckInStep();
        noteForce = null;
        bindOpenView(config);
    } else if (!previewBlocksLive && config.state === 'before_open') {
        initOpenStatus(config);
    } else if (
        !previewBlocksLive &&
        (document.getElementById('check-in-open-clock') || document.getElementById('check-in-live-status')) &&
        !document.getElementById('check-in-btn') &&
        !document.getElementById('check-in-missed-btn') &&
        !document.querySelector('#check-in-preview-stage .check-in-success')
    ) {
        initOpenStatus(config);
    }
    if (!config.previewMode) {
        initWindowStatePolling(config);
    }
    initLiveBoard(config);
}
